import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Loader2, Radio } from "lucide-react";
import { openDataUrl } from "@/lib/openDataUrl";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import { LivePhotoCaptureDialog } from "@/components/LivePhotoCaptureDialog";
import { livePhotoUploadAccept } from "@/lib/applePhotoImport";
import { mediaStill, processMediaFiles } from "@/lib/livePhotoMedia";

/**
 * Multi-photo uploader → array of compressed JPEG data URLs.
 *
 * Driver-Day "products" ticket use-case: optional, no AI validation, no cap.
 * Pick one or many photos in a single tap (mobile camera roll supports multi).
 *
 * Props:
 *  - value: string[]        current photo data URLs
 *  - onChange(string[])     called whenever the list changes
 *  - label                  caption above the grid
 *  - testId                 data-testid prefix
 *  - capture                "environment" to force rear camera (optional)
 *  - max                    soft cap (defaults to 12 — generous but bounded)
 */
export function MultiPhotoUploader({ value = [], onChange, label = "Photos", testId = "multi-photo", capture, max = 12, colonyId, sharedAcrossColonies = false, noLive = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [liveOpen, setLiveOpen] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const remainingSlots = Math.max(0, max - value.length);
    if (remainingSlots === 0) {
      toast.error(`You can attach up to ${max} photos per ticket.`);
      return;
    }
    const picked = Array.from(files);
    setBusy(true);
    try {
      const result = await processMediaFiles(picked, { max, existingCount: value.length, colonyId, sharedAcrossColonies, onProgress: setProgress });
      onChange([...(value || []), ...result.items]);
      if (result.ignoredVideos) toast.message("Select each MOV together with its matching iPhone photo.");
      if (result.capped) toast.message(`Only ${remainingSlots} more photo${remainingSlots === 1 ? "" : "s"} allowed.`);
    } catch (e) {
      toast.error(e.message || "Could not read image");
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2" data-testid={`${testId}-block`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="text-[10px] text-muted-foreground">{value.length}/{max}</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {value.map((src, idx) => (
          <div key={`${testId}-photo-${idx}-${src.slice(-20)}`} className="relative aspect-square border border-border rounded-sm overflow-hidden bg-secondary/30">
            <LivePhotoSurface value={src} alt={`${label} ${idx + 1}`} className="w-full h-full cursor-zoom-in" onActivate={() => openDataUrl(mediaStill(src))} testId={`${testId}-thumb-${idx}`} />
            <button
              type="button"
              aria-label="Remove photo"
              className="absolute top-1 right-1 w-6 h-6 rounded-sm bg-background/95 border border-border flex items-center justify-center text-destructive hover:bg-destructive hover:text-destructive-foreground transition"
              onClick={() => remove(idx)}
              data-testid={`${testId}-remove-${idx}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {value.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="aspect-square border-2 border-dashed border-border rounded-sm flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary transition disabled:opacity-60"
            data-testid={`${testId}-add`}
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
            <span className="text-[10px] uppercase tracking-wider">{busy ? (progress ? `${progress}%` : "Adding…") : "Add"}</span>
          </button>
        )}
        {value.length < max && !noLive && (
          <button type="button" onClick={() => setLiveOpen(true)} disabled={busy} className="aspect-square border-2 border-dashed border-border rounded-sm flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary transition disabled:opacity-60" data-testid={`${testId}-live`}><Radio className="w-5 h-5" /><span className="text-[10px] uppercase tracking-wider">Live</span></button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={noLive || capture ? "image/*" : livePhotoUploadAccept("image/*,video/quicktime,video/mp4,video/webm")}
        multiple
        capture={capture}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testId}-input`}
      />
      <LivePhotoCaptureDialog open={liveOpen} onOpenChange={setLiveOpen} onCaptured={handleFiles} testId={`${testId}-live-capture`} />
    </div>
  );
}
